import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  Box,
  Button,
  DividerHorizontal,
  HeroCard,
  Text,
  TextInput,
} from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectLogin } from '~/store/auth/login/selectors';
import { loginUser } from '~/store/auth/login/thunks';

interface FormData {
  email: string;
  password: string;
}

const schema = yup.object({
  email: yup.string().email('E-mail inválido').required('E-mail obrigatório'),
  password: yup.string().min(8, 'Mínimo 8 caracteres').required('Senha obrigatória'),
});

export default function LoginView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(selectLogin);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: FormData) => {
    const ok = await dispatch(
      loginUser({ username: data.email, password: data.password }),
    ).unwrap();
    if (ok) navigate('/app/home', { replace: true });
  };

  return (
    <div className="min-h-screen bg-intense-01 flex flex-col">
      <HeroCard className="rounded-b-large">
        <Box className="w-full px-small pt-huge pb-xl xl:max-w-[760px] mx-auto">
          <Box className="flex flex-row items-center gap-smaller">
            <div className="h-[56px] w-[56px] rounded-medium bg-intense-01 flex items-center justify-center overflow-hidden shadow-level1">
              <img
                src="/logo.png"
                alt="Paga Agro"
                className="h-full w-full object-contain"
              />
            </div>
            <Text className="text-paragraphLG color-intense-01">Paga Agro</Text>
          </Box>
          <Text className="mt-small text-display1 color-intense-01" as="p">
            Bem-vindo
            <br />
            de volta
          </Text>
          <Text className="mt-nano text-bodySM color-intense-01">
            Acesse sua conta para gerir os financiamentos da fazenda.
          </Text>
        </Box>
      </HeroCard>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-1 w-full xl:max-w-[440px] mx-auto px-small pt-medium pb-large"
      >
        <Box className="mt-middle w-full flex flex-col gap-small">
          <TextInput
            icon="icon-email"
            control={control}
            name="email"
            placeholder="E-mail"
            keyboardType="email-address"
            autoComplete="email"
            mask="email"
            errorMessage={errors.email?.message}
          />
          <TextInput
            icon="icon-security"
            control={control}
            name="password"
            placeholder="Senha"
            secureTextEntry
            autoComplete="current-password"
            errorMessage={errors.password?.message}
          />
          <Text
            className="w-full text-right color-primary-01 text-bodyLG"
            onClick={() => navigate('/auth/recovery/email')}
          >
            Esqueci minha senha
          </Text>
        </Box>

        <Button
          type="submit"
          fullWidth
          className="mt-middle"
          text="Entrar"
          rightIcon="icon-arrow-right"
          loading={loading}
        />

        <Box className="flex flex-row items-center mt-medium gap-smaller">
          <Box className="flex-1">
            <DividerHorizontal />
          </Box>
          <Text className="text-bodySM color-intense-05">ou</Text>
          <Box className="flex-1">
            <DividerHorizontal />
          </Box>
        </Box>

        <Text className="mt-large w-full text-center color-intense-05 text-bodyMD" as="p">
          Ainda não tem conta?{' '}
          <Text
            inline
            className="color-primary-01 text-bodyLG underline"
            onClick={() => navigate('/auth/register')}
          >
            Criar conta
          </Text>
        </Text>
      </form>
    </div>
  );
}
